import React, { useState } from "react";
import PropTypes from "prop-types";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import { FoldToggleButton } from "./MyWorkStyled";

/**
 * A collapsed-by-default block of rows inside a client card / category
 * window — "Waiting on client / vendor" (outside rows whose own
 * Chase/Overdue chip isn't already loud) and "Snoozed" (Matthew, 9/4).
 * Never shows a count.
 *
 * Open/closed persists the same way the existing By client / By
 * category toggle does (localStorage), one setting per KIND rather
 * than per card — "I've cleared today's snoozes" should open every
 * Snoozed fold at once, not eleven separate little triangles.
 * Shared by desktop (MyWorkTab) and mobile (MobileHome) so both
 * surfaces fold the same way.
 */

const STORAGE_KEYS = {
  outside: "myWorkFoldOutsideOpen",
  snoozed: "myWorkFoldSnoozedOpen",
};

const readOpen = (kind) => {
  try {
    return localStorage.getItem(STORAGE_KEYS[kind]) === "1";
  } catch {
    return false;
  }
};

const writeOpen = (kind, open) => {
  try {
    localStorage.setItem(STORAGE_KEYS[kind], open ? "1" : "0");
  } catch {
    // Private-mode storage failures shouldn't break the toggle.
  }
};

const WorkFold = ({ kind, title, rows, children }) => {
  const [open, setOpen] = useState(() => readOpen(kind));

  if (!rows || rows.length === 0) return null;

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      writeOpen(kind, next);
      return next;
    });
  };

  return (
    <div data-testid={`work-fold-${kind}`}>
      <FoldToggleButton type="button" onClick={toggle} aria-expanded={open}>
        {open ? (
          <CaretDown size={10} weight="bold" />
        ) : (
          <CaretRight size={10} weight="bold" />
        )}
        {title}
      </FoldToggleButton>
      {open && children}
    </div>
  );
};

WorkFold.propTypes = {
  kind: PropTypes.oneOf(["outside", "snoozed"]).isRequired,
  title: PropTypes.string.isRequired,
  rows: PropTypes.array,
  children: PropTypes.node,
};

export default WorkFold;
